import React from "react";
import { useSelector } from "react-redux";
import { IContextualMenuItem } from "@fluentui/react";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../src/taskpane/config/controlStyles";
import { Share20Regular } from "../../src/taskpane/components/_v2/components/SvgIcons/Share20Regular";
import { useClientGroupShareGroups } from "../../src/taskpane/components/hooks/useClientGroupShareGroups";
import { IClientGroupShareGroup } from "../../src/taskpane/services/clientGroupShareGroups";
import { IClientGroupShareSource } from "../../src/taskpane/services/clientGroupShareService";
import type { IClientGroupSearchSite } from "../../src/taskpane/components/helpers/sharepointSearch/clientGroupSearchScope";
import { CMShareWithGroupPanel, IShareWithGroupSeed } from "../Panels/CMShareWithGroupPanel";
import { isShareWithGroupSiteView, shareWithGroupSourceSite } from "./MenuItems/shareWithGroupEligibility";
import { AGGREGATE_SOURCE_LIST_ID, AGGREGATE_SOURCE_WEB_URL } from "./helpers/aggregateRowFields";

const siteKey = (url: string | undefined): string => (url ?? "").trim().replace(/\/+$/, "").toLowerCase();
const stripBraces = (value: string): string => (value ?? "").replace("{", "").replace("}", "");

/** The folder a row's file is in, library included: FileDirRef when the row carries it, else
 * FileRef's parent. The panel decides link names per folder with it. */
const folderOf = (row: any): string | undefined => {
    if (typeof row?.FileDirRef === "string" && row.FileDirRef) return row.FileDirRef;
    const fileRef: string = typeof row?.FileRef === "string" ? row.FileRef : "";
    const slash: number = fileRef.lastIndexOf("/");
    return slash > 0 ? fileRef.slice(0, slash) : undefined;
};

/** The selected rows by source library, in selection order: what a run shares from. */
const sharedSources = (rows: any[]): IClientGroupShareSource[] => {
    const byLibrary: Map<string, IClientGroupShareSource> = new Map<string, IClientGroupShareSource>();
    (rows ?? []).forEach(row => {
        const listId: string | undefined = row?.[AGGREGATE_SOURCE_LIST_ID];
        const uniqueId: string = stripBraces(String(row?.UniqueId ?? ""));
        if (!listId || !uniqueId) return;
        const key: string = String(listId).toLowerCase();
        const source: IClientGroupShareSource = byLibrary.get(key) ?? { seedWebUrl: row[AGGREGATE_SOURCE_WEB_URL], seedListId: listId, seedUniqueIds: [] };
        source.seedUniqueIds.push(uniqueId);
        byLibrary.set(key, source);
    });
    return Array.from(byLibrary.values());
};

export interface IUseAggregatedShareWithGroupArgs {
    /** The commandable selection (locked and vanished rows already left out). */
    selectedItems: any[];
    /** The site the view is open on. */
    siteId: string;
    siteWebUrl: string;
    siteDisplayName?: string;
    /** The view's client group sites: where rows of another site come from. */
    groupSites?: IClientGroupSearchSite[];
}

/**
 * "Share with group" in the All documents view. Offered when every selected row is a file that
 * can be shared and all come from one site (any of its libraries), with the groups of THAT site:
 * the same ones sharing from the site itself offers, whichever site the view is open on. Each
 * library's files are shared from their own library. The panel keeps the files and groups it was
 * opened with: a selection change behind it must not retarget the run.
 */
export const useAggregatedShareWithGroup = ({ selectedItems, siteId, siteWebUrl, siteDisplayName, groupSites }: IUseAggregatedShareWithGroupArgs): { shareWithGroup: IContextualMenuItem | null; shareWithGroupPanel: JSX.Element | null } => {
    const languageStrings = React.useContext(LanguageContext);
    const clientListUrl: string | undefined = useSelector((state: RootState) => state.tenantInfo?.clientListUrl);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.at(-1));
    const viewQualifies: boolean = !!clientListUrl?.trim() && isShareWithGroupSiteView(currentDirectory);

    // The one site the selected files come from, as this view knows it: its own site or one of
    // its group sites. Any other site (or a mixed selection) offers nothing and looks nothing up.
    const sourceSite: { id: string; name: string | undefined } | undefined = React.useMemo(() => {
        const url: string | undefined = viewQualifies ? shareWithGroupSourceSite(selectedItems, row => row?.[AGGREGATE_SOURCE_WEB_URL]) : undefined;
        if (!url) return undefined;
        const key: string = siteKey(url);
        if (siteId && siteKey(siteWebUrl) === key) return { id: siteId, name: siteDisplayName };
        const groupSite: IClientGroupSearchSite | undefined = (groupSites ?? []).find(site => !!site?.id && siteKey(site.webUrl) === key);
        return groupSite ? { id: groupSite.id, name: groupSite.displayName } : undefined;
    }, [viewQualifies, selectedItems, siteId, siteWebUrl, siteDisplayName, groupSites]);

    const { groups } = useClientGroupShareGroups(sourceSite?.id, !!sourceSite, sourceSite?.name?.trim() || languageStrings.ClientGroup);

    const [dialog, setDialog] = React.useState<{ rows: any[]; groups: IClientGroupShareGroup[] } | null>(null);

    const shareWithGroup: IContextualMenuItem | null = React.useMemo(() => (sourceSite && groups.length > 0
        ? {
            key: "aggShareWithGroup",
            text: languageStrings.ShareWithGroup,
            iconProps: {},
            onRenderIcon: () => <Share20Regular className={menuItemIconStyles} />,
            onClick: () => { setDialog({ rows: selectedItems, groups }); },
        }
        : null), [sourceSite, groups, selectedItems, languageStrings]);

    const seeds: IShareWithGroupSeed[] = React.useMemo(
        () => (dialog?.rows ?? []).map(row => ({ name: row?.FileLeafRef ?? "", fileType: row?.[".fileType"], folder: folderOf(row) })),
        [dialog]);
    const resolveSources = React.useCallback(async (): Promise<IClientGroupShareSource[]> => sharedSources(dialog?.rows ?? []), [dialog]);
    const closePanel = React.useCallback(() => setDialog(null), []);

    const shareWithGroupPanel: JSX.Element | null = dialog
        ? <CMShareWithGroupPanel panelIsOpen={true} closePanel={closePanel} groups={dialog.groups} seeds={seeds} resolveSources={resolveSources} />
        : null;

    return { shareWithGroup, shareWithGroupPanel };
};
